import { FileIcon, Info, Mail, Minus, Paperclip, Plus, RefreshCw, X } from "lucide-react";
import { ChangeEvent, CSSProperties, DragEvent, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { Button } from "../components/Button";
import { Card, Field, SectionHeader } from "../components/UI";
import {
  apiFetch,
  formatCurrency,
  getSlicerOptions,
  getUserId,
  requestProviderQuote,
  resolveAssetUrl,
  updateProfile,
  type QuoteResult,
  type SlicerColor,
  type SlicerMaterial,
  type SlicerOption,
  type SlicerOptions
} from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

type QuoteSelections = {
  material: string;
  color: string;
  infill: string;
  nozzle: string;
  support: string;
  supportStyle: string;
  infillPattern: string;
  topBottomLayers: number;
  extraRequests: string;
  quantity: number;
};

type QuoteUpload = {
  id: string;
  file: File;
  name: string;
  volume?: number;
  bbox?: { x: number; y: number; z: number };
  weight_grams?: number;
  price?: number;
  model_file_id?: number | null;
  stl_file?: string | null;
  status: "loading" | "loaded" | "quoted" | "error";
  costText: string;
  options: QuoteSelections;
  quote?: QuoteResult;
};

type OutletContext = {
  openLogin: () => void;
};

type TooltipContent = {
  tooltip?: string;
  tooltip_image?: string;
};

type SegmentOption = TooltipContent & {
  label: string;
  value: string;
};

const defaultSlicerOptions: SlicerOptions = {
  materials: [
    { name: "PLA", colors: ["White", "Black", "Red", "Blue", "Grey"] },
    { name: "PETG", colors: ["Transparent", "Black", "White", "Blue"] },
    { name: "TPU", colors: ["Black", "White", "Natural"] }
  ],
  infill_options: [
    { label: "Standard", value: "standard", percent: 20 },
    { label: "High", value: "high", percent: 40 },
    { label: "Fully Solid", value: "solid", percent: 100 }
  ],
  infill_patterns: [
    { label: "Gyroid", value: "gyroid" },
    { label: "Grid", value: "grid" },
    { label: "Triangles", value: "triangles" },
    { label: "Lines", value: "lines" },
    { label: "Honeycomb", value: "honeycomb" }
  ],
  support_styles: [
    { label: "Tree", value: "tree" },
    { label: "Organic", value: "organic" },
    { label: "Normal", value: "normal" }
  ],
  support_default: "yes",
  top_bottom_layers_default: 4
};

const defaultColorHexMap: Record<string, string> = {
  White: "#FFFFFF",
  Black: "#000000",
  Red: "#F44336",
  Blue: "#2196F3",
  Grey: "#9E9E9E",
  Gray: "#9E9E9E",
  Silver: "#BDBDBD",
  Transparent: "#FFFFFF88",
  Natural: "#F5F5DC",
  Green: "#4CAF50",
  Yellow: "#FFEB3B",
  Orange: "#FF9800",
  Purple: "#9C27B0"
};

const QUOTE_NOTES_LIMIT = 1500;

const QUOTE_LOADING_MESSAGES = [
  "Weighing your model",
  "Counting every gram",
  "Measuring material needs",
  "Checking its filament appetite",
  "Putting it on scale",
  "Figuring out the weight",
  "Calculating those grams",
  "Almost got the number",
  "Keep this tab open"
] as const;

type QuoteContactInfo = {
  email: string;
  phone: string;
  instagram: string;
};

const defaultQuoteContactInfo: QuoteContactInfo = {
  email: "support@protodeccan.com",
  phone: "+91 6360164984",
  instagram: "@protodeccan"
};

function readColorName(color: SlicerColor) {
  return typeof color === "string" ? color : color.name;
}

function readColorHex(color: SlicerColor) {
  if (typeof color === "string") return defaultColorHexMap[color] || "#CCCCCC";
  return color.hex || defaultColorHexMap[color.name] || "#CCCCCC";
}

function formatWeight(value?: number) {
  if (!value || Number.isNaN(value)) return "";
  if (value < 1) return `${value.toFixed(2)}g`;
  if (value < 10) return `${value.toFixed(1)}g`;
  return `${Math.round(value)}g`;
}

function formatDimensions(bbox?: { x: number; y: number; z: number }) {
  if (!bbox) return "";
  return `${bbox.x} x ${bbox.y} x ${bbox.z} mm`;
}

function hasTooltip(content?: TooltipContent) {
  return Boolean(content?.tooltip || content?.tooltip_image);
}

function QuoteTooltip({ content }: { content?: TooltipContent }) {
  if (!hasTooltip(content)) return null;
  const imageUrl = content?.tooltip_image ? resolveAssetUrl(content.tooltip_image) : "";
  return (
    <span className="quote-tooltip">
      <span className="quote-tooltip-trigger">
        <Info size={13} strokeWidth={2.1} />
      </span>
      <span className="quote-tooltip-content" role="tooltip">
        {content?.tooltip ? <span>{content.tooltip}</span> : null}
        {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : null}
      </span>
    </span>
  );
}

function LabelWithTooltip({ label, tooltip }: { label: string; tooltip?: TooltipContent }) {
  return (
    <div className="selector-label q-label">
      <span>{label}</span>
      <QuoteTooltip content={tooltip} />
    </div>
  );
}

function SegmentedControl({
  ariaLabel,
  onChange,
  options,
  value
}: {
  ariaLabel: string;
  onChange: (value: string) => void;
  options: SegmentOption[];
  value: string;
}) {
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const style = {
    "--segment-count": options.length,
    "--segment-index": selectedIndex
  } as CSSProperties;

  return (
    <div className="segment-group" role="radiogroup" aria-label={ariaLabel} style={style}>
      <span className="segment-indicator" aria-hidden="true" />
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={["segment-item", selected ? "selected" : "", hasTooltip(option) ? "has-tooltip" : ""].filter(Boolean).join(" ")}
            onClick={() => onChange(option.value)}
          >
            <span>{option.label}</span>
            <QuoteTooltip content={option} />
          </button>
        );
      })}
    </div>
  );
}

async function readFileBuffer(file: File) {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error("Could not read STL file."));
    reader.readAsArrayBuffer(file);
  });
}

async function extractFileMetrics(file: File) {
  const buffer = await readFileBuffer(file);
  const loader = new STLLoader();
  const geometry = loader.parse(buffer);
  geometry.computeBoundingBox();

  const pos = geometry.attributes.position;
  let volume = 0;
  for (let index = 0; index < pos.count; index += 3) {
    const v1 = new THREE.Vector3().fromBufferAttribute(pos, index);
    const v2 = new THREE.Vector3().fromBufferAttribute(pos, index + 1);
    const v3 = new THREE.Vector3().fromBufferAttribute(pos, index + 2);
    volume += v1.dot(v2.cross(v3)) / 6;
  }

  const size = new THREE.Vector3();
  geometry.boundingBox?.getSize(size);

  const result = {
    volume: Math.abs(volume),
    bbox: {
      x: Math.round(size.x),
      y: Math.round(size.y),
      z: Math.round(size.z)
    }
  };

  geometry.dispose();
  return result;
}

export function QuotePage() {
  const navigate = useNavigate();
  const { addQuoteToCart, addToast, cart, pendingFiles, setPendingFiles, setUser, toasts, user } = useAppState();
  const { openLogin } = useOutletContext<OutletContext>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const quoteResultsRef = useRef<HTMLDivElement | null>(null);

  const [slicerOptions, setSlicerOptions] = useState<SlicerOptions>(defaultSlicerOptions);
  const [uploads, setUploads] = useState<QuoteUpload[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [material, setMaterial] = useState("PLA");
  const [color, setColor] = useState("White");
  const [infill, setInfill] = useState("standard");
  const [support, setSupport] = useState("yes");
  const [supportStyle, setSupportStyle] = useState("tree");
  const [infillPattern, setInfillPattern] = useState("gyroid");
  const [nozzle, setNozzle] = useState("0.4");
  const [topBottomLayers, setTopBottomLayers] = useState(4);
  const [extraRequests, setExtraRequests] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasRequestedQuote, setHasRequestedQuote] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [loadingDots, setLoadingDots] = useState(1);
  const [quoteEmailValue, setQuoteEmailValue] = useState(user?.email || "");
  const [quoteEmailPromptOpen, setQuoteEmailPromptOpen] = useState(false);
  const [quoteEmailSaving, setQuoteEmailSaving] = useState(false);
  const [quoteEmailEnabled, setQuoteEmailEnabled] = useState(false);
  const [contactInfo, setContactInfo] = useState<QuoteContactInfo>(defaultQuoteContactInfo);
  const [contactPopupOpen, setContactPopupOpen] = useState(false);
  const cartReminderShownRef = useRef(false);
  const notesLimitToastShownRef = useRef(false);

  const activeUpload = uploads[activeIndex];
  const materials = slicerOptions.materials?.length ? slicerOptions.materials : defaultSlicerOptions.materials;
  const infillOptions = slicerOptions.infill_options?.length ? slicerOptions.infill_options : defaultSlicerOptions.infill_options;
  const infillPatterns = slicerOptions.infill_patterns?.length ? slicerOptions.infill_patterns : defaultSlicerOptions.infill_patterns;
  const supportStyles = slicerOptions.support_styles?.length ? slicerOptions.support_styles : defaultSlicerOptions.support_styles;
  const currentMaterial = materials.find((entry) => entry.name === material) || materials[0];
  const colorOptions = currentMaterial?.colors?.length ? currentMaterial.colors : materials[0]?.colors || [];

  const total = useMemo(
    () => uploads.reduce((sum, item) => sum + (item.status === "quoted" ? Number(item.price || 0) : 0), 0),
    [uploads]
  );
  const totalWeight = useMemo(
    () => uploads.reduce((sum, item) => sum + (item.status === "quoted" ? Number(item.weight_grams || 0) : 0), 0),
    [uploads]
  );
  const hasResolvedQuoteResponse = hasRequestedQuote && !loading && uploads.some((item) => item.status === "quoted" || item.status === "error");
  const hasFailedUploads = hasResolvedQuoteResponse && uploads.some((item) => item.status === "error");
  const canShowActions = uploads.length > 0 && total > 0 && uploads.every((item) => item.status === "quoted");

  useEffect(() => {
    let cancelled = false;
    getSlicerOptions()
      .then((data) => {
        if (cancelled) return;
        setSlicerOptions(data);
        const firstMaterial = data.materials?.[0]?.name || "PLA";
        setMaterial((current) => current || firstMaterial);
        setSupportStyle(data.support_styles?.[0]?.value || "tree");
        setTopBottomLayers(Number(data.top_bottom_layers_default) || 4);
        setSupport(data.support_default || "yes");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Partial<{ contact_email: string; contact_phone: string; contact_instagram: string }>>("/about-us")
      .then((data) => {
        if (cancelled) return;
        setContactInfo({
          email: data.contact_email || defaultQuoteContactInfo.email,
          phone: data.contact_phone || defaultQuoteContactInfo.phone,
          instagram: data.contact_instagram || defaultQuoteContactInfo.instagram
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ enabled: boolean }>("/quotes/email-config")
      .then((data) => {
        if (!cancelled) setQuoteEmailEnabled(Boolean(data.enabled));
      })
      .catch(() => {
        if (!cancelled) setQuoteEmailEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const materialEntry = materials.find((entry) => entry.name === material);
    if (materialEntry) return;
    const nextMaterial = materials[0]?.name;
    if (nextMaterial) setMaterial(nextMaterial);
  }, [material, materials]);

  useEffect(() => {
    const colorNames = colorOptions.map(readColorName);
    if (colorNames.includes(color)) return;
    const nextColor = colorNames[0];
    if (nextColor) setColor(nextColor);
  }, [color, colorOptions]);

  useEffect(() => {
    if (!pendingFiles.length) return;
    acceptFiles(pendingFiles);
    setPendingFiles([]);
  }, [pendingFiles, setPendingFiles]);

  useEffect(() => {
    if (pendingFiles.length || uploads.length || !cart.length) return;
    if (cartReminderShownRef.current) return;
    if (toasts.some((toast) => toast.type === "cart-reminder")) return;
    addToast("You have items in your cart", "cart-reminder");
    cartReminderShownRef.current = true;
  }, [addToast, cart.length, pendingFiles.length, toasts, uploads.length]);

  useEffect(() => {
    const current = uploads[activeIndex];
    if (!current?.options) return;
    // The form controls are a projection of the active file's saved options.
    // We only hydrate local state on file switch; control handlers write changes
    // back explicitly. A previous auto-sync effect wrote stale local values into
    // the newly selected file, causing color/material values to fight each other.
    setMaterial(current.options.material);
    setColor(current.options.color);
    setInfill(current.options.infill);
    setNozzle(current.options.nozzle);
    setSupport(current.options.support);
    setSupportStyle(current.options.supportStyle);
    setInfillPattern(current.options.infillPattern);
    setTopBottomLayers(current.options.topBottomLayers);
    setExtraRequests(current.options.extraRequests);
    setQuantity(current.options.quantity);
  }, [activeIndex, uploads]);

  useEffect(() => {
    if (!hasResolvedQuoteResponse) return;
    const timeoutId = window.setTimeout(() => {
      quoteResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timeoutId);
  }, [hasResolvedQuoteResponse]);

  useEffect(() => {
    if (!loading) {
      setLoadingMessageIndex(0);
      setLoadingDots(1);
      return;
    }

    const messageInterval = window.setInterval(() => {
      setLoadingMessageIndex((current) => (current + 1) % QUOTE_LOADING_MESSAGES.length);
    }, 7800);

    const dotsInterval = window.setInterval(() => {
      setLoadingDots((current) => (current % 3) + 1);
    }, 450);

    return () => {
      window.clearInterval(messageInterval);
      window.clearInterval(dotsInterval);
    };
  }, [loading]);

  function currentSelections(): QuoteSelections {
    return {
      material,
      color,
      infill,
      nozzle,
      support,
      supportStyle,
      infillPattern,
      topBottomLayers,
      extraRequests,
      quantity
    };
  }

  function updateUpload(id: string, updater: (upload: QuoteUpload) => QuoteUpload) {
    setUploads((current) => current.map((item) => (item.id === id ? updater(item) : item)));
  }

  function acceptFiles(nextFiles: FileList | File[]) {
    const accepted = Array.from(nextFiles).filter(
      (file) => file.name.toLowerCase().endsWith(".stl") && file.size >= 84
    );

    if (!accepted.length) {
      addToast("Please upload valid non-empty .stl files", "error");
      return;
    }

    const selectionSnapshot = currentSelections();
    const newUploads = accepted.map<QuoteUpload>((file) => ({
      id: `qf_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file,
      name: file.name,
      status: "loading",
      costText: "Loading...",
      options: selectionSnapshot
    }));

    const startIndex = uploads.length;
    setUploads((current) => [...current, ...newUploads]);
    setActiveIndex(startIndex);

    newUploads.forEach((upload) => {
      extractFileMetrics(upload.file)
        .then((metrics) => {
          updateUpload(upload.id, (current) => ({
            ...current,
            volume: metrics.volume,
            bbox: metrics.bbox,
            status: current.status === "quoted" ? current.status : "loaded",
            costText: current.status === "quoted" ? current.costText : "Ready"
          }));
        })
        .catch(() => {
          updateUpload(upload.id, (current) => ({
            ...current,
            status: "error",
            costText: "Error"
          }));
        });
    });
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleInput(event: ChangeEvent<HTMLInputElement>) {
    if (event.currentTarget.files) acceptFiles(event.currentTarget.files);
    event.currentTarget.value = "";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    acceptFiles(event.dataTransfer.files);
  }

  function removeUpload(index: number) {
    setUploads((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      setHasRequestedQuote((wasRequested) => (next.length > 0 ? wasRequested : false));
      setActiveIndex((currentIndex) => {
        if (next.length <= 0) return 0;
        if (currentIndex > index) return currentIndex - 1;
        return Math.max(0, Math.min(currentIndex, next.length - 1));
      });
      return next;
    });
  }

  function applyOptionsToActiveUpload(partial: Partial<QuoteSelections>) {
    setUploads((current) =>
      current.map((item, index) =>
        index === activeIndex
          ? {
              ...item,
              options: {
                ...item.options,
                ...partial
              },
              // A quote is only valid for the exact settings used to calculate it.
              // If the user changes material/color/etc. after quoting, force a new estimate.
              status: item.status === "quoted" ? "loaded" : item.status,
              quote: item.status === "quoted" ? undefined : item.quote,
              price: item.status === "quoted" ? undefined : item.price,
              model_file_id: item.status === "quoted" ? undefined : item.model_file_id,
              stl_file: item.status === "quoted" ? undefined : item.stl_file,
              costText: item.status === "quoted" ? "Recalculate" : item.costText
            }
          : item
      )
    );
  }

  function handleInfillChange(nextInfill: string) {
    setInfill(nextInfill);
    applyOptionsToActiveUpload({ infill: nextInfill });
  }

  function handleNozzleChange(nextNozzle: string) {
    setNozzle(nextNozzle);
    applyOptionsToActiveUpload({ nozzle: nextNozzle });
  }

  function handleSupportChange(nextSupport: string) {
    setSupport(nextSupport);
    applyOptionsToActiveUpload({ support: nextSupport });
  }

  function handleSupportStyleChange(nextSupportStyle: string) {
    setSupportStyle(nextSupportStyle);
    applyOptionsToActiveUpload({ supportStyle: nextSupportStyle });
  }

  function handleInfillPatternChange(nextInfillPattern: string) {
    setInfillPattern(nextInfillPattern);
    applyOptionsToActiveUpload({ infillPattern: nextInfillPattern });
  }

  function handleTopBottomLayersChange(nextLayers: number) {
    const resolvedLayers = Math.max(1, Math.min(20, nextLayers || 4));
    setTopBottomLayers(resolvedLayers);
    applyOptionsToActiveUpload({ topBottomLayers: resolvedLayers });
  }

  function handleExtraRequestsChange(nextExtraRequests: string) {
    if (nextExtraRequests.length > QUOTE_NOTES_LIMIT && !notesLimitToastShownRef.current) {
      addToast(`Print notes are too long. Keep them under ${QUOTE_NOTES_LIMIT} characters.`, "error");
      notesLimitToastShownRef.current = true;
    }
    if (nextExtraRequests.length <= QUOTE_NOTES_LIMIT) {
      notesLimitToastShownRef.current = false;
    }
    const resolvedRequests = nextExtraRequests.slice(0, QUOTE_NOTES_LIMIT);
    setExtraRequests(resolvedRequests);
    applyOptionsToActiveUpload({ extraRequests: resolvedRequests });
  }

  function handleMaterialChange(nextMaterial: string) {
    const nextMaterialEntry = materials.find((entry) => entry.name === nextMaterial) || materials[0];
    const nextColorOptions = nextMaterialEntry?.colors || [];
    const nextColorNames = nextColorOptions.map(readColorName);
    const resolvedColor = nextColorNames.includes(color) ? color : nextColorNames[0] || color;

    setMaterial(nextMaterial);
    setColor(resolvedColor);
    applyOptionsToActiveUpload({
      material: nextMaterial,
      color: resolvedColor
    });
  }

  function handleColorChange(nextColor: string) {
    setColor(nextColor);
    applyOptionsToActiveUpload({ color: nextColor });
  }

  async function calculateQuote() {
    if (!uploads.length) {
      addToast("Please upload an STL file first.", "error");
      return;
    }

    setHasRequestedQuote(true);
    setLoading(true);
    setUploads((current) =>
      current.map((item) => ({
        ...item,
        status: item.status === "error" ? item.status : "loading",
        costText: "Calculating..."
      }))
    );

    try {
      for (const item of uploads) {
        const options = item.options || currentSelections();
        try {
          const quote = await requestProviderQuote({
            file: item.file,
            material: options.material,
            color: options.color,
            infill: options.infill,
            support: options.support,
            supportStyle: options.supportStyle,
            infillPattern: options.infillPattern,
            nozzle: options.nozzle,
            topBottomLayers: options.topBottomLayers,
            extraRequests: options.extraRequests,
            quantity: options.quantity,
            userId: getUserId(user)
          });

          updateUpload(item.id, (current) => ({
            ...current,
            status: "quoted",
            costText: `₹ ${Math.round(quote.price)}`,
            price: quote.price,
            model_file_id: quote.model_file_id,
            stl_file: quote.stl_file,
            weight_grams: quote.weight,
            volume: quote.volume ? quote.volume * 1000 : current.volume,
            quote
          }));
        } catch (error) {
          updateUpload(item.id, (current) => ({
            ...current,
            status: "error",
            costText: error instanceof Error ? error.message : "Error"
          }));
        }
      }
    } finally {
      setLoading(false);
    }
  }

  function scrollQuoteEstimateIntoView() {
    window.requestAnimationFrame(() => {
      quoteResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  function handleQuotePrimaryAction() {
    if (hasResolvedQuoteResponse && canShowActions) {
      handleCheckout();
      return;
    }
    scrollQuoteEstimateIntoView();
    void calculateQuote();
  }

  function getQuotedItems() {
    return uploads.filter((item) => item.status === "quoted" && item.price && item.quote);
  }

  async function performSendQuoteToEmail(emailToken: string) {
    const quotedItems = getQuotedItems();
    await apiFetch("/quotes/send-email", {
      method: "POST",
      token: emailToken,
      body: JSON.stringify({
        items: quotedItems.map((item) => ({
          filename: item.name,
          price: item.price,
          volume: item.volume ? `${(item.volume / 1000).toFixed(1)} cm³` : "",
          weight: item.weight_grams ? `${item.weight_grams.toFixed(2)} g` : "",
          settings: item.options
        }))
      })
    });
  }

  async function sendQuoteToEmail() {
    const quotedItems = uploads.filter((item) => item.status === "quoted" && item.price && item.quote);
    if (!quotedItems.length) {
      addToast("Calculate a quote first by letting at least one STL finish parsing.", "error");
      return;
    }
    if (!user?.token) {
      openLogin();
      return;
    }
    if (!user.email) {
      setQuoteEmailValue("");
      setQuoteEmailPromptOpen(true);
      return;
    }

    try {
      await performSendQuoteToEmail(user.token);
      addToast("Quote sent to your email successfully!", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to send quote email", "error");
    }
  }

  async function saveEmailAndSendQuote() {
    if (!user?.token) {
      openLogin();
      return;
    }
    const userId = getUserId(user);
    if (!userId) return;
    const nextEmail = quoteEmailValue.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      addToast("Enter a valid email address.", "error");
      return;
    }

    setQuoteEmailSaving(true);
    try {
      const nextUser = await updateProfile(userId, user.token, {
        full_name: user.full_name || "",
        phone: user.phone || "",
        email: nextEmail
      });
      setUser({
        ...user,
        full_name: nextUser.full_name,
        phone: nextUser.phone,
        email: nextUser.email
      });
      await performSendQuoteToEmail(user.token);
      setQuoteEmailPromptOpen(false);
      addToast("Quote sent to your email successfully!", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to send quote email", "error");
    } finally {
      setQuoteEmailSaving(false);
    }
  }

  function handleCheckout() {
    const quotedItems = uploads.filter((item) => item.status === "quoted" && item.quote);
    if (!quotedItems.length) {
      addToast("Calculate a quote first.", "error");
      return;
    }
    quotedItems.forEach((item) => {
      if (item.quote) addQuoteToCart(item.quote);
    });
    navigate("/cart");
  }

  function renderAttachmentMeta(upload: QuoteUpload) {
    const parts = [
      upload.volume ? `${(upload.volume / 1000).toFixed(1)} cm³` : "",
      formatDimensions(upload.bbox),
      upload.weight_grams ? formatWeight(upload.weight_grams) : ""
    ].filter(Boolean);

    if (parts.length) return parts.join(" · ");
    if (upload.status === "loading") return "Reading model";
    if (upload.status === "error") return upload.costText || "Could not read model";
    return "Ready to configure";
  }

  function getTitleTooltip(label: string): TooltipContent | undefined {
    return slicerOptions.title_tooltips?.find(
      (entry) => entry.title.trim().toLowerCase() === label.trim().toLowerCase()
    );
  }

  function getOptionTooltip(values: SlicerOption[], value: string): TooltipContent | undefined {
    return values.find((entry) => entry.value === value);
  }

  function tooltipFor(label: string, optionTooltip?: TooltipContent): TooltipContent | undefined {
    const titleTooltip = getTitleTooltip(label);
    return hasTooltip(titleTooltip) ? titleTooltip : optionTooltip;
  }

  function renderSelectorGroup(
    id: string,
    label: string,
    value: string,
    values: SlicerOption[] | string[],
    onChange: (next: string) => void,
    disabled = false
  ) {
    const options = values.map<SegmentOption>((entry) => {
      if (typeof entry === "string") return { label: entry, value: entry };
      return {
        label: entry.label,
        value: entry.value,
        tooltip: entry.tooltip,
        tooltip_image: entry.tooltip_image
      };
    });

    return (
      <div style={{ marginTop: id === "q-material" ? 0 : "var(--space-md)" }}>
        <LabelWithTooltip label={label} tooltip={getTitleTooltip(label)} />
        {disabled ? null : <SegmentedControl ariaLabel={label} value={value} options={options} onChange={onChange} />}
      </div>
    );
  }

  return (
    <section>
      <SectionHeader
        title="Configure your 3D print"
        description="Upload a model, choose how it should print, and get a clear estimate before checkout."
      />
      <div className="quote-layout">
        <div className={uploads.length ? "quote-viewer file-loaded" : "quote-viewer"}>
          <div
            className="dropzone"
            id="stl-dropzone"
            role="button"
            tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openFilePicker();
              }
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="dropzone-icon">↑</div>
            <div className="dropzone-text" id="stl-dropzone-text">Drop your 3D model here</div>
            <div className="dropzone-sub">or click to browse</div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".stl"
            multiple
            onChange={handleInput}
            style={{ display: "none" }}
          />

          <div id="viewer-container" className={uploads.length ? "" : "hidden"}>
            {uploads.length ? (
              <Suspense fallback={<div className="stl-viewer-state"><strong>Loading STL preview</strong></div>}>
                <STLViewer file={activeUpload?.file} color={color} mode="quote" />
              </Suspense>
            ) : null}
          </div>
        </div>

        <div className="quote-sidebar">
          <div id="quote-file-queue" className={uploads.length ? "qf-list" : "qf-list hidden"}>
            {uploads.map((upload, index) => (
              <div
                key={upload.id}
                className={index === activeIndex ? "qf-row active" : "qf-row"}
                onClick={() => setActiveIndex(index)}
              >
                <div className="qf-row-icon" aria-hidden="true">
                  <FileIcon size={18} strokeWidth={2.1} />
                </div>
                <div className="qf-row-info">
                  <div className="qf-row-name" title={upload.name}>{upload.name}</div>
                  <div className="qf-row-meta">{renderAttachmentMeta(upload)}</div>
                </div>
                <button
                  className="qf-row-remove"
                  type="button"
                  title="Remove File"
                  aria-label={`Remove ${upload.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeUpload(index);
                  }}
                >
                  <X size={15} strokeWidth={2.2} />
                </button>
              </div>
            ))}
            <div className="qf-add-wrap">
              <button className="qf-add-link" type="button" onClick={openFilePicker}>
                <Paperclip size={14} strokeWidth={2.2} aria-hidden="true" />
                Add another model
              </button>
            </div>
          </div>

          <Card className="print-config-card">
            {renderSelectorGroup(
              "q-material",
              "Material",
              material,
              materials.map((entry: SlicerMaterial) => ({
                label: entry.name,
                value: entry.name,
                tooltip: entry.tooltip,
                tooltip_image: entry.tooltip_image
              })),
              handleMaterialChange
            )}

            <div style={{ marginTop: "var(--space-md)" }}>
              <LabelWithTooltip label="Color" tooltip={getTitleTooltip("Color")} />
              <div id="q-color" className="color-swatch-group" data-selected={color} role="radiogroup" aria-label="Color">
                {colorOptions.map((entry) => {
                  const name = readColorName(entry);
                  const hex = readColorHex(entry);
                  return (
                    <button
                      key={name}
                      className={name === color ? "color-swatch-btn active" : "color-swatch-btn"}
                      data-value={name}
                      data-hex={hex}
                      title={name}
                      aria-label={name}
                      type="button"
                      role="radio"
                      aria-checked={name === color}
                      onClick={() => handleColorChange(name)}
                    >
                      <span className="color-swatch-circle" style={{ background: hex }} />
                    </button>
                  );
                })}
              </div>
            </div>

            {renderSelectorGroup("q-infill", "Infill", infill, infillOptions, handleInfillChange)}

            <div style={{ marginTop: "var(--space-md)" }}>
              <LabelWithTooltip label="Nozzle" tooltip={getTitleTooltip("Nozzle")} />
              <SegmentedControl
                ariaLabel="Nozzle"
                value={nozzle}
                options={[
                  { label: "0.4 mm", value: "0.4" },
                  { label: "0.6 mm", value: "0.6" }
                ]}
                onChange={handleNozzleChange}
              />
            </div>

            <details className="advanced-options-accordion">
              <summary className="advanced-options-summary">Advanced Options</summary>
              <div className="advanced-options-panel">
                <label className="support-switch-field">
                  <LabelWithTooltip label="Support" tooltip={getTitleTooltip("Support")} />
                  <input
                    type="checkbox"
                    checked={support === "yes"}
                    onChange={(event) => handleSupportChange(event.currentTarget.checked ? "yes" : "no")}
                  />
                  <span className="support-switch" aria-hidden="true" />
                </label>

                {support === "yes" ? (
                  <div className="support-style-field">
                    <LabelWithTooltip label="Support Style" tooltip={tooltipFor("Support Style", getOptionTooltip(supportStyles, supportStyle))} />
                    <select
                      className="form-control select-control"
                      id="q-support-style"
                      value={supportStyle}
                      onChange={(event) => handleSupportStyleChange(event.currentTarget.value)}
                    >
                      {supportStyles.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div>
                  <LabelWithTooltip label="Infill Pattern" tooltip={tooltipFor("Infill Pattern", getOptionTooltip(infillPatterns, infillPattern))} />
                  <select
                    className="form-control select-control"
                    id="q-infill-pattern"
                    value={infillPattern}
                    onChange={(event) => handleInfillPatternChange(event.currentTarget.value)}
                  >
                    {infillPatterns.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                    ))}
                  </select>
                </div>

                <div>
                  <LabelWithTooltip label="Solid top/bottom layers" tooltip={getTitleTooltip("Solid top/bottom layers")} />
                  <div className="number-stepper">
                    <button type="button" aria-label="Decrease solid layers" onClick={() => handleTopBottomLayersChange(topBottomLayers - 1)}>
                      <Minus size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                    <span>{topBottomLayers}</span>
                    <button type="button" aria-label="Increase solid layers" onClick={() => handleTopBottomLayersChange(topBottomLayers + 1)}>
                      <Plus size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </details>

            <Field label="Print notes (optional)" className="print-notes-field" style={{ marginTop: "var(--space-md)", marginBottom: 0 }}>
              <textarea
                className="form-control"
                id="q-extra-requests"
                rows={4}
                placeholder="Add colour, finish, strength, or delivery instructions..."
                value={extraRequests}
                onChange={(event) => handleExtraRequestsChange(event.currentTarget.value)}
              />
            </Field>

            <div className="estimate-summary" id="quote-results-area" ref={quoteResultsRef}>
              {hasResolvedQuoteResponse ? (
                <>
                  <div className="estimate-summary-head">
                    <span>Estimated total</span>
                    <strong id="cost-total">
                      {hasFailedUploads
                        ? "Some files failed"
                        : total <= 0
                          ? "Pricing unavailable"
                          : formatCurrency(total)}
                    </strong>
                    <small>
                      {hasFailedUploads
                        ? "Some files failed, please contact us for assistance."
                        : totalWeight > 0
                          ? `${formatWeight(totalWeight)} · Taxes calculated at checkout`
                          : "Taxes calculated at checkout"}
                    </small>
                  </div>
                  {hasFailedUploads ? (
                    <div className="quote-error-help" role="status">
                      <span>We could not estimate one or more STL files.</span>
                      <button type="button" onClick={() => setContactPopupOpen(true)}>
                        Contact us
                      </button>
                    </div>
                  ) : null}
                  <div className="cost-breakdown" id="cost-breakdown-list">
                    {uploads.map((upload) => (
                      <div className="cost-row" key={upload.id} style={{ color: upload.status === "error" ? "var(--status-error)" : undefined }}>
                        <span>
                          {upload.status === "quoted" && (upload.volume || upload.weight_grams)
                            ? [upload.volume ? `${Math.round(upload.volume).toLocaleString()} mm³` : "", upload.weight_grams ? formatWeight(upload.weight_grams) : ""].filter(Boolean).join(", ")
                            : upload.name}
                        </span>
                        <span>
                          {upload.status === "quoted"
                            ? Number(upload.price || 0) > 0
                              ? `₹ ${Math.round(Number(upload.price || 0))}`
                              : "Pricing unavailable"
                            : upload.status === "error"
                              ? "Error"
                              : upload.costText}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              {loading ? (
                <div className="quote-loading-copy" aria-live="polite">
                  <span>{QUOTE_LOADING_MESSAGES[loadingMessageIndex]}</span>
                  <span className="quote-loading-dots" aria-hidden="true">{".".repeat(loadingDots)}</span>
                </div>
              ) : null}
              {!hasFailedUploads ? (
                <Button className="btn-block" id="btn-get-quote" onClick={handleQuotePrimaryAction} disabled={loading} loading={loading}>
                  {loading ? "Slicing..." : hasResolvedQuoteResponse && canShowActions ? "Proceed to cart" : "Calculate estimate"}
                </Button>
              ) : null}
              {quoteEmailEnabled && hasResolvedQuoteResponse && canShowActions ? (
                <Button className="btn-block" type="button" variant="secondary" icon={<Mail size={16} aria-hidden="true" />} onClick={sendQuoteToEmail}>
                  Email this quote
                </Button>
              ) : null}
              {quoteEmailPromptOpen ? (
                <div className="quote-email-capture">
                  <div>
                    <strong>Email for this quote</strong>
                    <span>We will save it to your profile for next time.</span>
                  </div>
                  <input
                    className="form-control"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={quoteEmailValue}
                    onChange={(event) => setQuoteEmailValue(event.currentTarget.value)}
                  />
                  <Button className="btn-block" type="button" loading={quoteEmailSaving} onClick={saveEmailAndSendQuote}>
                    {quoteEmailSaving ? "Sending..." : "Save email and send"}
                  </Button>
                </div>
              ) : null}
              {hasResolvedQuoteResponse ? (
                <div className="estimate-secondary-actions">
                  <button type="button" className={hasFailedUploads ? "recalculate-link recalculate-link-prominent" : "recalculate-link"} onClick={calculateQuote} disabled={loading}>
                    <RefreshCw size={14} strokeWidth={2.1} aria-hidden="true" />
                    Recalculate estimate
                  </button>
                </div>
              ) : null}
            </div>
          </Card>
              </div>
      </div>
      {contactPopupOpen ? (
        <div className="modal-backdrop quote-contact-backdrop" role="presentation" onMouseDown={() => setContactPopupOpen(false)}>
          <div className="modal-panel quote-contact-modal" role="dialog" aria-modal="true" aria-labelledby="quote-contact-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="modal-close" aria-label="Close contact details" onClick={() => setContactPopupOpen(false)}>
              <X size={18} aria-hidden="true" />
            </button>
            <h2 id="quote-contact-title">Contact us for assistance</h2>
            <p>Send us the failed STL file name and we will help estimate it manually.</p>
            <div className="about-contact-row quote-contact-row">
              <div className="about-contact-item">
                <span className="contact-label"><Mail size={14} aria-hidden="true" /></span>
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </div>
              <div className="about-contact-item">
                <span className="contact-label">☎</span>
                <a href={`tel:${contactInfo.phone.replace(/[^\d+]/g, "")}`}>{contactInfo.phone}</a>
              </div>
              <div className="about-contact-item">
                <span className="contact-label">@</span>
                <span>{contactInfo.instagram}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
